import {
  cloneDataOrNull, DataModel, DataModelBuilder, isSameData, isSameList,
  SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { CartItemDto } from './cart-item.js';
import { TicketType } from '../ticket-type.js';
import { ElectronicDeliveryMethod } from '../electronic-delivery-method.js';
import { TicketTemplate } from '../ticket-template/index.js';
import { Discount } from '../discounts/discount.js';
import { VersionRange } from '../../core/version-range.js';
import { DiscountCodeBase } from '../../../contracts/discount-code.js';

export class OfflineEventCartEntryDto extends DataModel {
  static readonly TYPE_ID: string = '5e39949f4b824b84953eb3160251a5b2';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
      public readonly eventId: string | null,
      public readonly items: CartItemDto[],
      public readonly voucher: DiscountCodeBase | null,
      public readonly discount: Discount | null,
      public readonly supplementaryTicket: TicketTemplate | null,
      public readonly ticketType: TicketType,
      public readonly requireTaxIdentifier: boolean,
      public readonly promoterId: string | null,
      public readonly note: string | null,
      public readonly deliveryMethod: ElectronicDeliveryMethod,
  ) {
    super();
  }

  get $typeId() {
    return OfflineEventCartEntryDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(OfflineEventCartEntryDto.SCHEMA_VERSION);
  }

  static _create(args: {
      eventId: string | null, // backing field will be made non nullable in next version!
      items: CartItemDto[],
      voucher: DiscountCodeBase | null,
      discount: Discount | null,
      supplementaryTicket: TicketTemplate | null,
      ticketType: TicketType,
      requireTaxIdentifier: boolean,
      promoterId: string | null,
      note: string | null,
      deliveryMethod: ElectronicDeliveryMethod,
  }): OfflineEventCartEntryDto {
    return new OfflineEventCartEntryDto(
        args.eventId,
        args.items,
        args.voucher,
        args.discount,
        args.supplementaryTicket,
        args.ticketType,
        args.requireTaxIdentifier,
        args.promoterId,
        args.note,
        args.deliveryMethod,
    );
  }

  static create(args: {
      eventId: string, // non-nullable. backing field will be made non nullable in next version!
      items: CartItemDto[],
      voucher: DiscountCodeBase | null,
      discount: Discount | null,
      supplementaryTicket: TicketTemplate | null,
      ticketType: TicketType,
      requireTaxIdentifier: boolean,
      promoterId: string | null,
      note: string | null,
      deliveryMethod: ElectronicDeliveryMethod,
  }): OfflineEventCartEntryDto {
    return OfflineEventCartEntryDto._create(args);
  }

  static fromLegacy(args: {
      eventId: string | null,
      items: CartItemDto[],
      voucher: DiscountCodeBase | null,
      discount: Discount | null,
      supplementaryTicket: TicketTemplate | null,
      ticketType: TicketType,
      requireTaxIdentifier: boolean,
      promoterId: string | null,
      note: string | null,
      deliveryMethod: ElectronicDeliveryMethod,
  }): OfflineEventCartEntryDto {
    return OfflineEventCartEntryDto._create(args);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineEventCartEntryDto {
    return this._create({
      eventId: MapUtils.getStringOrNull(map, 'eventId'),
      items: MapUtils.getList<CartItemDto>(map, 'items', (e) => {
        return context.fromData(e as SerializedData);
      }),
      voucher: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'voucher')),
      discount: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'discount')),
      supplementaryTicket: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'supplementaryTicket')),
      ticketType: toEnumFromString(MapUtils.getString(map, 'ticketType'), TicketType),
      requireTaxIdentifier: MapUtils.getBoolean(map, 'requireTaxIdentifier'),
      promoterId: MapUtils.getStringOrNull(map, 'promoterId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'items': this.items.map((e) => context.toData(e)),
      'voucher': context.toDataOrNull(this.voucher),
      'discount': context.toDataOrNull(this.discount),
      'supplementaryTicket': context.toDataOrNull(this.supplementaryTicket),
      'ticketType': fromEnumAsString(this.ticketType),
      'requireTaxIdentifier': this.requireTaxIdentifier,
      'promoterId': this.promoterId,
      'note': this.note,
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
    };
  }

  clone(): this {
    return OfflineEventCartEntryDto._create({
      eventId: this.eventId,
      items: this.items.map((e) => e.clone()),
      voucher: cloneDataOrNull(this.voucher),
      discount: cloneDataOrNull(this.discount),
      supplementaryTicket: cloneDataOrNull(this.supplementaryTicket),
      ticketType: this.ticketType,
      requireTaxIdentifier: this.requireTaxIdentifier,
      promoterId: this.promoterId,
      note: this.note,
      deliveryMethod: this.deliveryMethod,
    }) as this;
  }

  isSame(other: OfflineEventCartEntryDto): boolean {
    if(other === this) {
      return true;
    }

    return other.eventId === this.eventId &&
      isSameList(other.items, this.items, isSameData) &&
      isSameData(other.voucher, this.voucher) &&
      isSameData(other.discount, this.discount) &&
      other.ticketType === this.ticketType &&
      other.requireTaxIdentifier === this.requireTaxIdentifier &&
      other.promoterId === this.promoterId &&
      other.note === this.note &&
      other.deliveryMethod === this.deliveryMethod &&
      isSameData(other.supplementaryTicket, this.supplementaryTicket);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OfflineEventCartEntryDto;
  }

  static assertType(order: DataModel): order is OfflineEventCartEntryDto {
    return order instanceof OfflineEventCartEntryDto;
  }
}

export class OfflineEventCartEntryDtoBuilder extends DataModelBuilder<OfflineEventCartEntryDto> {
  build(context: SerializationContext, map: SerializedData): OfflineEventCartEntryDto {
    return OfflineEventCartEntryDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineEventCartEntryDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineEventCartEntryDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 voucher type was changed from `Voucher` to `DiscountCodeBase`.
    // No migration is required, class `Voucher` extends class `DiscountCodeBase`.
    if(version < 2) version = 2;

    // In version 3 the factory constructor was updated to only allow non-null event ID
    if(version < 3) version = 3;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
